import _ from "lodash";

/**
 * Nome e referência de etapa: como o nome é sanitizado, como a referência ao
 * resultado é escrita e como ela é reescrita quando o fluxo muda.
 *
 * Módulo separado de `functions.js` de propósito: aqui só entra manipulação de
 * texto, sem nenhum import da árvore de componentes. É o que mantém estas
 * regras — todas sutis e todas testáveis isoladamente — cobertas por teste.
 */

/** Nomes que o executor já ocupa na raiz do contexto de dados da execução. */
const RESERVED_STEP_NAMES = ["trigger", "steps", "loop", "moment"];

/**
 * Caracteres que quebrariam a referência gerada — `steps.[nome]` no template e
 * `steps['nome']` no código. Acentos são permitidos: as duas formas aceitam
 * qualquer caractere fora desta lista.
 */
const INVALID_STEP_NAME_CHARS = /[[\]{}'"`\\.]/g;

export const sanitizeStepNameInput = (value) =>
    String(value ?? "").replace(INVALID_STEP_NAME_CHARS, "");

/** Nome como ele é gravado e referenciado: sem espaços redundantes. */
export const normalizeStepName = (value) =>
    sanitizeStepNameInput(value).trim().replace(/\s+/g, " ");

/**
 * Motivo pelo qual `name` não pode nomear o passo `step`, ou null se pode.
 * Nome vazio é válido: a etapa volta a se chamar pelo nome padrão do tipo.
 */
export const validateStepName = (actions, step, name) => {
    if (!name) return null;
    if (RESERVED_STEP_NAMES.includes(name.toLowerCase())) {
        return `"${name}" é um nome reservado pela automação — escolha outro nome para a etapa.`;
    }
    if (/^\d+$/.test(name)) {
        return `"${name}" não pode ser usado: um nome só de números se confunde com a referência por posição (steps.${name}).`;
    }
    const duplicated = (actions ?? []).some((item, index) =>
        index !== step &&
        normalizeStepName(item?.name).toLowerCase() === name.toLowerCase()
    );
    if (duplicated) {
        return `Já existe outra etapa chamada "${name}" — o nome precisa ser único para servir de referência.`;
    }
    return null;
};

/**
 * Referência ao resultado de uma etapa nomeada. O executor publica o resultado
 * em `steps[nome]`, então a forma de template usa segmento literal do
 * Handlebars (`steps.[nome]`) e a de código, índice por string —  as duas
 * aceitam nome com espaço e acento.
 */
export const buildStepReference = (name, js = false) =>
    js ? `steps['${name}']` : `steps.[${name}]`;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Sem distinção de caixa: o backend exige o texto exato no save, mas uma
// referência digitada com a caixa errada antes dessa regra existir ainda
// aponta para a mesma etapa — reescrever também a conserta.
const rewriteStepReferences = (text, from, templateRef, codeRef) => {
    const escaped = escapeRegExp(from);
    return text
        .replace(new RegExp(`steps\\.\\[\\s*${escaped}\\s*\\]`, "gi"), templateRef)
        .replace(new RegExp(`steps\\[\\s*(['"\`])${escaped}\\1\\s*\\]`, "gi"), codeRef);
};

/**
 * Se algum texto dentro de `value` satisfaz `predicate`. Percorre o valor cru,
 * não o JSON dele: serializar escaparia as aspas duplas e a referência escrita
 * como `steps["3"]` deixaria de casar.
 */
const someString = (value, predicate) => {
    if (typeof value === "string") return predicate(value);
    if (Array.isArray(value)) return value.some((item) => someString(item, predicate));
    if (_.isPlainObject(value)) return Object.values(value).some((item) => someString(item, predicate));
    return false;
};

const mapStrings = (value, fn) => {
    if (typeof value === "string") return fn(value);
    if (Array.isArray(value)) return value.map((item) => mapStrings(item, fn));
    if (_.isPlainObject(value)) return _.mapValues(value, (item) => mapStrings(item, fn));
    return value;
};

/**
 * Reescreve, em qualquer texto dentro de `value`, as referências à etapa
 * `from` para `to`. É o que mantém a configuração das etapas posteriores
 * válida quando uma etapa referenciada é renomeada.
 */
export const retargetStepNameInValue = (value, from, to) =>
    mapStrings(value, (text) =>
        rewriteStepReferences(text, from, `steps.[${to}]`, `steps['${to}']`));

/**
 * Reescreve as referências à etapa `from` para a forma posicional
 * (`stepNumber` em base 1). É o destino quando o nome da etapa é apagado:
 * a referência pelo nome deixaria de resolver, mas a etapa continua lá.
 */
export const retargetStepNameToPosition = (value, from, stepNumber) =>
    mapStrings(value, (text) =>
        rewriteStepReferences(text, from, `steps.${stepNumber}`, `steps['${stepNumber}']`));

/**
 * Nome impossível para uma etapa — o ponto é recusado em nome de etapa —, então
 * uma referência a ele nunca resolve por acidente e o save sempre a recusa.
 */
export const REMOVED_STEP_REFERENCE = "etapa.removida";

/** `steps.N` (template) e `steps['N']` (código), as duas formas que o editor gera. */
const POSITIONAL_TEMPLATE_REF = /steps\.(\d+)/g;
const POSITIONAL_CODE_REF = /steps\[\s*(['"`])(\d+)\1\s*\]/g;

/**
 * Reescreve as referências posicionais de `value` depois que a etapa na posição
 * `removedPosition` (base 1) sai do fluxo.
 *
 * O executor publica o resultado em `steps[N]` com N reatribuído a cada save
 * (`step: index + 1`), então remover uma etapa renumera todas as seguintes:
 * sem reescrever, `steps.4` continuaria escrito igual e passaria a apontar para
 * a etapa que era a 5 — em silêncio, sem erro em lugar nenhum. A referência à
 * própria etapa removida não tem para onde apontar: vira uma referência
 * explicitamente inválida, que o save recusa nomeando quem a usa.
 */
export const retargetPositionalReferencesAfterRemoval = (value, removedPosition) => {
    const shift = (position) => (position > removedPosition ? position - 1 : position);

    return mapStrings(value, (text) =>
        text
            .replace(POSITIONAL_TEMPLATE_REF, (match, digits) => {
                const position = Number(digits);
                return position === removedPosition
                    ? `steps.[${REMOVED_STEP_REFERENCE}]`
                    : `steps.${shift(position)}`;
            })
            .replace(POSITIONAL_CODE_REF, (match, quote, digits) => {
                const position = Number(digits);
                // O marcador sai sempre em aspa simples: o extrator de
                // referência por nome do save reconhece `'` e `"`, mas não
                // crase — com crase o marcador passaria despercebido.
                return position === removedPosition
                    ? `steps['${REMOVED_STEP_REFERENCE}']`
                    : `steps[${quote}${shift(position)}${quote}]`;
            }));
};

/**
 * Índices das etapas cuja configuração usa o resultado da etapa na posição
 * `position` (base 1) — o que a confirmação de exclusão precisa avisar.
 */
export const findStepsReferencingPosition = (actions, position) => {
    // `(?!\d)` evita que a posição 1 case com `steps.12`.
    const templateRef = new RegExp(`steps\\.${position}(?!\\d)`);
    const codeRef = new RegExp(`steps\\[\\s*(['"\`])${position}\\1\\s*\\]`);
    const referencesIt = (text) => templateRef.test(text) || codeRef.test(text);

    return (actions ?? []).reduce((found, item, index) => {
        if (someString(item?.config, referencesIt)) {
            found.push(index);
        }
        return found;
    }, []);
};
